<template>
    <!-- Tarjeta de login con el diseño de Mi cuenta (BkField + bkCuenta.css). -->
    <section class="bk-card" aria-labelledby="lg-title">
        <header class="bk-card-head">
            <span class="bk-step" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
            </span>
            <div>
                <h2 id="lg-title" class="bk-card-title">{{ $t('auth_nuevo.login.card_title') }}</h2>
                <p class="bk-card-desc">{{ $t('auth_nuevo.login.card_desc') }}</p>
            </div>
        </header>

        <form novalidate @submit.prevent="handleSubmit">
            <div v-if="alerta" class="bk-auth-alert" role="alert"><p>{{ alerta }}</p></div>

            <BkField id="lg-email" v-model.trim="formData.email" type="email" inputmode="email" autocomplete="username"
                :label="$t('auth_nuevo.f.email')" :maxlength="150" :error="errorDe('email')" @input="alerta = ''" @blur="tocar('email')" />

            <BkField id="lg-password" v-model="formData.password" :type="ver ? 'text' : 'password'" autocomplete="current-password"
                :label="$t('auth_nuevo.f.password')" :maxlength="100" :error="errorDe('password')" @input="alerta = ''" @blur="tocar('password')">
                <template #accion>
                    <button type="button" :class="['bk-eye', { 'is-on': ver }]"
                        :aria-label="ver ? $t('auth_nuevo.hide') : $t('auth_nuevo.show')"
                        :title="ver ? $t('auth_nuevo.hide') : $t('auth_nuevo.show')"
                        :aria-pressed="String(ver)" @click="ver = !ver">
                        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M2 12s3.8-6.5 10-6.5S22 12 22 12s-3.8 6.5-10 6.5S2 12 2 12z" />
                            <circle cx="12" cy="12" r="3.1" />
                            <path v-if="ver" d="M4 20 L20 4" />
                        </svg>
                    </button>
                </template>
            </BkField>

            <div class="bk-auth-row">
                <router-link :to="{ name: 'recuperar' }" class="bk-auth-link">{{ $t('auth_nuevo.login.forgot') }}</router-link>
            </div>

            <button type="submit" class="bk-btn bk-btn--primary bk-auth-submit" :disabled="isLoading">
                <span v-if="isLoading" class="bk-spinner" aria-hidden="true"></span>
                {{ isLoading ? $t('auth_nuevo.login.loading') : $t('auth_nuevo.login.submit') }}
            </button>
        </form>

        <GoogleButton />

        <p class="bk-auth-foot">
            {{ $t('auth_nuevo.login.no_account') }}
            <router-link :to="{ name: 'register', params: { step: 1 } }">{{ $t('auth_nuevo.login.create') }}</router-link>
        </p>
    </section>
</template>

<script>
import { mapActions } from "vuex";
import GoogleButton from "@/pages/Registration/GoogleButton.vue";
import BkField from "@/components/BankInformation/BkField.vue";
import { askMissingUserData, redirectDespuesDeEntrar } from "@/Utils/Utils";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CAMPOS = ['email', 'password'];

export default {
    name: "Login",
    components: { BkField, GoogleButton },
    data() {
        return {
            formData: { email: '', password: '' },
            ver: false,
            isLoading: false,
            intento: false,
            tocados: {},
            // Error de la API (credenciales, sin conexión): va arriba, en la tarjeta.
            alerta: ''
        };
    },
    computed: {
        errores() {
            const e = {};
            if (!this.formData.email) e.email = this.$t('validations.required');
            else if (!EMAIL_RE.test(this.formData.email)) e.email = this.$t('auth_nuevo.err.email');
            // Sin mínimo ni máximo: hay cuentas viejas con contraseñas de otro largo.
            if (!this.formData.password) e.password = this.$t('validations.required');
            return e;
        }
    },
    methods: {
        ...mapActions(["login", "createSession"]),

        tocar(campo) {
            this.$set(this.tocados, campo, true);
        },
        // El error se muestra al salir del campo o al intentar entrar.
        errorDe(campo) {
            return (this.tocados[campo] || this.intento) && this.errores[campo] ? this.errores[campo] : '';
        },

        async handleSubmit() {
            if (this.isLoading) return;
            this.intento = true;
            this.alerta = '';
            const conError = CAMPOS.find(c => this.errores[c]);
            if (conError) {
                const el = document.getElementById(`lg-${conError}`);
                if (el) el.focus();
                return;
            }

            this.isLoading = true;
            try {
                const response = await this.login({ correo: this.formData.email, password: this.formData.password });
                await this.createSession(response);
                // Con datos faltantes sigue el registro; si no, a donde iba o a Mi perfil.
                if (!askMissingUserData(response, this.$router)) {
                    redirectDespuesDeEntrar(this.$router);
                }
            } catch (e) {
                this.alerta = this.textoDeError(e);
            } finally {
                this.isLoading = false;
            }
        },

        // La API contesta { correo: ['validation.credentials_invalid'], hash: [...] }.
        textoDeError(e) {
            if (!e || !e.response) {
                console.error('[Login] sin respuesta de la API', e);
                return this.$t('auth_nuevo.err.network');
            }
            const data = e.response.data || {};
            const codigos = [].concat(data.correo || [], data.hash || []);
            if (codigos.some(c => String(c).includes('credentials_invalid'))) return this.$t('auth_nuevo.err.credentials');
            // Otros códigos con texto propio (validations.error-email-validation.required, …).
            for (const [campo, front] of [['correo', 'email'], ['hash', 'password']]) {
                for (const c of [].concat(data[campo] || [])) {
                    const clave = `validations.error-${front}-${c}`;
                    if (this.$te(clave)) return this.$t(clave);
                }
            }
            return this.$t('auth_nuevo.err.generic');
        }
    }
};
</script>
